import * as React from 'react';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridSlots,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PaymentIcon from '@mui/icons-material/Payment';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { getInstallmentsWithPagination, type Installment } from '../data/orders';
import { getAllEmployees, type Employee } from '../data/employees';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';
import InstallmentPaymentDialog from './InstallmentPaymentDialog';
import DateRangeFilter from './DateRangeFilter';
import CustomTablePagination from './CustomTablePagination';

interface EmployeeInstallmentsTableProps {
  userId?: number;
  onDataChange?: () => void;
  showEmployeeFilter?: boolean;
}

export default function EmployeeInstallmentsTable({ userId: userIdProp, onDataChange, showEmployeeFilter = false }: EmployeeInstallmentsTableProps) {
  const { t, direction } = useLanguage();
  const { user } = useAuth();
  const [employees, setEmployees] = React.useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<number | null>(null);
  const [installments, setInstallments] = React.useState<Installment[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [rowCount, setRowCount] = React.useState(0);
  const [totalAmount, setTotalAmount] = React.useState<number | undefined>(undefined);
  const [selectedInstallmentForPayment, setSelectedInstallmentForPayment] = React.useState<Installment | null>(null);
  const [installmentPaymentDialogOpen, setInstallmentPaymentDialogOpen] = React.useState(false);
  const [dateFrom, setDateFrom] = React.useState<string | null>(null);
  const [dateTo, setDateTo] = React.useState<string | null>(null);
  const [columnMenuAnchor, setColumnMenuAnchor] = React.useState<null | HTMLElement>(null);

  // Fetch employees for the autocomplete filter (only for GM & BM roles)
  const canAccessEmployeeFilter = user?.role === 'GM' || user?.role === 'BM';
  React.useEffect(() => {
    if (!showEmployeeFilter || !canAccessEmployeeFilter) return;
    const fetchEmployees = async () => {
      try {
        const employeesList = await getAllEmployees();
        setEmployees(employeesList);
      } catch (err) {
        console.error('Failed to fetch employees:', err);
      }
    };
    fetchEmployees();
  }, [showEmployeeFilter, canAccessEmployeeFilter]);

  // Use selected employee ID from filter, or fall back to prop, or role-based logic for Employee role
  const userId = userIdProp ?? selectedEmployeeId ?? (user?.role === 'Employee' ? user.id : undefined);

  // Default column visibility - specify which columns to hide by default
  const defaultColumnVisibility: GridColumnVisibilityModel = {
    customerMobile: false,  // Hidden by default
    employee_name: false,   // Hidden by default
    created_at: false,      // Hidden by default
  };

  // Column visibility state - load from localStorage or use defaults
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>(() => {
    const saved = localStorage.getItem('employeeInstallmentsColumnVisibility');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return defaultColumnVisibility;
      }
    }
    return defaultColumnVisibility;
  });

  // Save column visibility to localStorage whenever it changes
  const handleColumnVisibilityModelChange = React.useCallback(
    (newModel: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(newModel);
      localStorage.setItem('employeeInstallmentsColumnVisibility', JSON.stringify(newModel));
    },
    [],
  );

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await getInstallmentsWithPagination({
        paginationModel,
        userId,
        status: 'DUE',
        sortBy: 'due_date',
        sortOrder: 'asc',
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });

      setInstallments(result.items);
      setRowCount(result.itemCount);
      setTotalAmount(result.totalAmount);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, paginationModel, dateFrom, dateTo]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInstallmentPayment = React.useCallback((installment: Installment) => {
    setSelectedInstallmentForPayment(installment);
    setInstallmentPaymentDialogOpen(true);
  }, []);

  const handleInstallmentPaymentClose = React.useCallback(() => {
    setInstallmentPaymentDialogOpen(false);
    setSelectedInstallmentForPayment(null);
    loadData(); // Refresh the data after payment
    if (onDataChange) {
      onDataChange(); // Notify parent component of data change
    }
  }, [loadData, onDataChange]);

  const handleColumnMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  }, []);

  const handleColumnMenuClose = React.useCallback(() => {
    setColumnMenuAnchor(null);
  }, []);

  const handleToggleColumn = React.useCallback(
    (field: string) => {
      setColumnVisibilityModel((prev) => {
        const newModel = {
          ...prev,
          [field]: prev[field] === false ? true : false,
        };
        localStorage.setItem('employeeInstallmentsColumnVisibility', JSON.stringify(newModel));
        return newModel;
      });
    },
    [],
  );

  // Define available columns for visibility toggle
  const availableColumns = React.useMemo(() => [
    { field: 'installment_number', label: t('orders.installmentNumber') || 'Installment #' },
    { field: 'customer', label: t('orders.customer') || 'Customer Name' },
    { field: 'customerMobile', label: t('orders.customerNumber') || 'Customer Mobile' },
    { field: 'employee_name', label: t('employees.name') || 'Employee Name' },
    { field: 'amount', label: t('orders.amount') || 'Amount' },
    { field: 'due_date', label: t('orders.dueDate') || 'Due Date' },
    { field: 'created_at', label: t('orders.createdAt') || 'Created At' },
  ], [t]);

  const formatAmount = (amount: string | number | undefined) => {
    if (!amount) return '0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return numAmount.toFixed(2);
  };

  const isRtl = direction === 'rtl';

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'installment_number',
        headerName: t('orders.installmentNumber') || 'Installment #',
        width: 100,
        minWidth: 90,
        flex: 0.6,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row: Installment) => row.installment_number || row.seq || '-',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'customer',
        headerName: t('orders.customer') || 'Customer Name',
        width: 120,
        minWidth: 100,
        flex: 0.9,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row: Installment) => {
          const customer = row.order?.customer;
          return customer?.name || row.order?.customerName || 'N/A';
        },
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'customerMobile',
        headerName: t('orders.customerNumber') || 'Customer Mobile',
        width: 110,
        minWidth: 100,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row: Installment) => {
          const customer = row.order?.customer;
          const mobileNumber = customer?.mobile_number || row.order?.customerNumber || 'N/A';
          return mobileNumber === 'N/A' ? 'N/A' : mobileNumber.replace(/\s+/g, '');
        },
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'employee_name',
        headerName: t('employees.name') || 'Employee Name',
        width: 120,
        minWidth: 100,
        flex: 0.9,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row: Installment) => {
          return row.employee_name || row.order?.user?.name || 'N/A';
        },
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'amount',
        headerName: t('orders.amount') || 'Amount',
        width: 110,
        minWidth: 100,
        flex: 0.7,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row: Installment) => {
          const amount = typeof row.amount === 'string'
            ? parseFloat(row.amount)
            : row.amount;
          return amount || 0;
        },
        renderCell: (params) => {
          const currencySymbol = direction === 'rtl' ? 'د.إ' : 'AED';
          return `${formatAmount(params.value)} ${currencySymbol}`;
        },
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'due_date',
        headerName: t('orders.dueDate') || 'Due Date',
        width: 100,
        minWidth: 90,
        flex: 0.7,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row: Installment) =>
          dayjs(row.due_date).format('YYYY-MM-DD'),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'created_at',
        headerName: t('orders.createdAt') || 'Created At',
        width: 120,
        minWidth: 110,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row: Installment) =>
          dayjs(row.created_at).format('YYYY-MM-DD HH:mm'),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'actions',
        headerName: t('orders.actions') || 'Actions',
        width: 80,
        minWidth: 80,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: (params) => {
          const installment = params.row as Installment;
          const isPaid = installment.status === 'PAID' || installment.paid === true;
          return (
            <Tooltip title={t('orders.payInstallment') || 'Pay Installment'}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInstallmentPayment(installment);
                  }}
                  disabled={isPaid}
                  sx={{
                    '&:disabled': {
                      opacity: 0.3,
                    },
                  }}
                >
                  <PaymentIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [t, direction, handleInstallmentPayment, isRtl],
  );

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Tooltip title="Toggle Columns" placement="bottom" enterDelay={1000}>
          <IconButton
              size="small"
              aria-label="toggle columns"
              onClick={handleColumnMenuOpen}
          >
            <ViewColumnIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      </Box>
      <Box
        sx={{
          flex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
            />
            {showEmployeeFilter && canAccessEmployeeFilter && (
              <Autocomplete
                dir={direction}
                size="small"
                options={employees}
                getOptionLabel={(option) => option.name}
                value={employees.find((e) => e.id === selectedEmployeeId) || null}
                onChange={(_, newValue) => {
                  setSelectedEmployeeId(newValue ? newValue.id : null);
                }}
                noOptionsText={t('common.noOptions')}
                slotProps={{
                  popupIndicator: { sx: { display: 'none' } },
                  clearIndicator: {
                    sx: {
                      p: 0.5,
                      border: 0,
                      bgcolor: "transparent",
                      '&:hover': { bgcolor: 'action.hover' },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('orders.employee') || 'Employee'}
                    inputProps={{
                      ...params.inputProps,
                      style: { textAlign: direction === 'rtl' ? 'right' : 'left' },
                    }}
                  />
                )}
                sx={{ minWidth: 180, flex: '0 1 auto' }}
              />
            )}
          </Box>
          {totalAmount !== undefined && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <Typography variant="body1" fontWeight="medium">
                {t('orders.totalDueAmount') || 'Total Due Amount'}:
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatAmount(totalAmount)} {direction === 'rtl' ? 'د.إ' : 'AED'}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <DataGrid
              rows={installments}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
              paginationMode="server"
              pageSizeOptions={[25, 50, 75, 100]}
              rowCount={rowCount}
              loading={isLoading}
              disableRowSelectionOnClick
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              hideFooter
              localeText={{
                noRowsLabel: t('orders.noInstallments') || 'No installments found',
                noResultsOverlayLabel: t('orders.noInstallments') || 'No installments found',
              }}
              sx={{
                height: '100%',
                width: '100%',
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    backgroundColor: 'rgba(2, 139, 203, 0.05)',
                  },
                },
                // Hide column separator (resize handle)
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                // Disable column header dragging
                '& .MuiDataGrid-columnHeader': {
                  cursor: 'default !important',
                },
              }}
            />
          </Box>
          <CustomTablePagination
            paginationModel={paginationModel}
            rowCount={rowCount}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 75, 100]}
          />
        </Box>
      </Box>

      {/* Installment Payment Dialog */}
      <InstallmentPaymentDialog
        open={installmentPaymentDialogOpen}
        installment={selectedInstallmentForPayment}
        onClose={handleInstallmentPaymentClose}
        onSuccess={handleInstallmentPaymentClose}
      />

      {/* Column Visibility Popover */}
      <Popover
        open={Boolean(columnMenuAnchor)}
        anchorEl={columnMenuAnchor}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: direction === 'rtl' ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: direction === 'rtl' ? 'left' : 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Show/Hide Columns
          </Typography>
          <Stack spacing={0.5}>
            {availableColumns.map((col) => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={columnVisibilityModel[col.field] !== false}
                    onChange={() => handleToggleColumn(col.field)}
                    size="small"
                  />
                }
                label={col.label}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}

